import React from 'react';
import { View, Text, Svg, Line, Polyline, Circle } from '@react-pdf/renderer';
import { calcularFcjEsperado, calcularFcmj } from '../../utils/fcjCalculo';

const PADDING = { top: 16, right: 16, bottom: 28, left: 34 };
const LABEL_FONT_SIZE = 6.5;
const MIN_LABEL_GAP = 8.5;

// Exibe um número com 2 casas decimais e vírgula (padrão BR), ou null se ausente/inválido.
function formatMpa(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value).toFixed(2).replace('.', ',');
}

// Amostra uma curva (Fcmj ou Fcj Esperado) em pontos de idade de 7 em 7 dias até idadeMax.
function amostrarCurva({ fck, ponderadorTipoCimento, idadeMax, calcular }) {
  if (fck == null || ponderadorTipoCimento == null) return [];
  const idades = [];
  for (let dia = 1; dia <= idadeMax; dia += 7) idades.push(dia);
  if (idades[idades.length - 1] !== idadeMax) idades.push(idadeMax);

  return idades
    .map((idade) => ({ idade, valor: calcular({ fck, idade, ponderadorTipoCimento }) }))
    .filter((ponto) => ponto.valor != null);
}

// Gráfico de evolução da resistência (MPa x dias) de um caminhão, desenhado com primitivos
// SVG do react-pdf (não há canvas/DOM disponível durante o render do PDF).
// Legenda: preto = Fcj medido, verde = Fcmj (resistência média esperada), vermelho = Fcj Esperado.
function CurvaResistenciaChart({
  pontosMedidos = [], // [{ idade, fcj }] apenas CPs com resultado (linha preta some se vazio)
  fck,
  ponderadorTipoCimento,
  width = 760,
  height = 320,
}) {
  const idadeMaxMedida = pontosMedidos.reduce((max, p) => Math.max(max, p.idade || 0), 0);
  const idadeMax = Math.max(28, Math.ceil(idadeMaxMedida / 7) * 7 || 28);

  const curvaFcmj = amostrarCurva({ fck, ponderadorTipoCimento, idadeMax, calcular: calcularFcmj });
  const curvaFcjEsperado = amostrarCurva({ fck, ponderadorTipoCimento, idadeMax, calcular: calcularFcjEsperado });

  const plotW = width - PADDING.left - PADDING.right;
  const plotH = height - PADDING.top - PADDING.bottom;

  const maxYBruto = Math.max(
    10,
    (fck || 0) * 1.3,
    ...pontosMedidos.map((p) => p.fcj || 0),
    ...curvaFcmj.map((p) => p.valor),
    ...curvaFcjEsperado.map((p) => p.valor)
  );
  // Folga de 8% para o ponto/rótulo mais alto não ficar colado na borda superior do gráfico.
  const maxY = maxYBruto * 1.08;

  const scaleX = (idade) => PADDING.left + (idade / idadeMax) * plotW;
  const scaleY = (mpa) => PADDING.top + plotH - (mpa / maxY) * plotH;

  // Marcações do eixo X de 7 em 7 dias (14 em 14 se a idade máxima for muito alta) até idadeMax,
  // para que o eixo sempre acompanhe visualmente a maior idade medida do caminhão.
  const stepX = idadeMax > 70 ? 14 : 7;
  const ticksIdade = [];
  for (let dia = 0; dia <= idadeMax; dia += stepX) ticksIdade.push(dia);
  if (ticksIdade[ticksIdade.length - 1] !== idadeMax) ticksIdade.push(idadeMax);

  const stepY = Math.ceil(maxY / 5 / 5) * 5 || 5;
  const ticksMpa = [];
  for (let mpa = 0; mpa <= maxY; mpa += stepY) ticksMpa.push(mpa);
  // Garante uma marcação em 45 MPa quando os valores ultrapassam esse limiar, mesmo que não
  // caia exatamente no passo calculado (referência de leitura importante para o usuário).
  if (maxY > 45 && !ticksMpa.includes(45)) {
    ticksMpa.push(45);
    ticksMpa.sort((a, b) => a - b);
  }

  const toPolyline = (pontos, xKey, yKey) =>
    pontos
      .slice()
      .sort((a, b) => a[xKey] - b[xKey])
      .map((p) => `${scaleX(p[xKey])},${scaleY(p[yKey])}`)
      .join(' ');

  const pontosMedidosValidos = pontosMedidos.filter((p) => p.idade != null && p.fcj != null);
  const pointsFcj = toPolyline(pontosMedidosValidos, 'idade', 'fcj');
  const pointsFcmj = toPolyline(curvaFcmj, 'idade', 'valor');
  const pointsFcjEsperado = toPolyline(curvaFcjEsperado, 'idade', 'valor');

  // Posiciona os rótulos "XX,XX MPa" evitando sobreposição: quando vários pontos têm fcj
  // próximo (comum em CPs rompidos na mesma idade), empurra os rótulos mais baixos para
  // baixo até garantir um espaçamento vertical mínimo em relação ao rótulo anterior. O
  // ponto/círculo medido não é afetado — só a posição do texto do rótulo.
  const rotulosFcj = pontosMedidosValidos
    .map((p, index) => ({ p, index, labelY: scaleY(p.fcj) - 5 }))
    .sort((a, b) => a.labelY - b.labelY)
    .reduce((acc, rotulo, i) => {
      if (i === 0) return [rotulo];
      const anterior = acc[i - 1];
      const labelY = Math.max(rotulo.labelY, anterior.labelY + MIN_LABEL_GAP);
      return [...acc, { ...rotulo, labelY }];
    }, []);

  return (
    <View style={{ marginTop: 6 }} wrap={false}>
      <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 3 }}>
        Curva de Evolução da Resistência (MPa x Dias)
      </Text>

      <Svg width={width} height={height}>
        {/* Grid horizontal (MPa) */}
        {ticksMpa.map((mpa) => (
          <Line
            key={`grid-y-${mpa}`}
            x1={PADDING.left}
            y1={scaleY(mpa)}
            x2={width - PADDING.right}
            y2={scaleY(mpa)}
            stroke="#e5e5e5"
            strokeWidth={0.5}
          />
        ))}

        {/* Grid vertical (dias) */}
        {ticksIdade.map((dia) => (
          <Line
            key={`grid-x-${dia}`}
            x1={scaleX(dia)}
            y1={PADDING.top}
            x2={scaleX(dia)}
            y2={height - PADDING.bottom}
            stroke="#e5e5e5"
            strokeWidth={0.5}
          />
        ))}

        {/* Eixos */}
        <Line x1={PADDING.left} y1={PADDING.top} x2={PADDING.left} y2={height - PADDING.bottom} stroke="#999" strokeWidth={0.75} />
        <Line x1={PADDING.left} y1={height - PADDING.bottom} x2={width - PADDING.right} y2={height - PADDING.bottom} stroke="#999" strokeWidth={0.75} />

        {/* Rótulos dos eixos */}
        {ticksMpa.map((mpa) => (
          <Text key={`label-y-${mpa}`} x={2} y={scaleY(mpa) + 2.5} style={{ fontSize: 5.5, fill: '#666' }}>
            {mpa}
          </Text>
        ))}
        {ticksIdade.map((dia) => (
          <Text key={`label-x-${dia}`} x={scaleX(dia) - 5} y={height - PADDING.bottom + 9} style={{ fontSize: 5.5, fill: '#666' }}>
            {`${dia}d`}
          </Text>
        ))}

        {/* Curva vermelha: Fcj Esperado */}
        {pointsFcjEsperado ? (
          <Polyline points={pointsFcjEsperado} stroke="#dc2626" strokeWidth={1} strokeDasharray="4 3" fill="none" />
        ) : null}

        {/* Curva verde: Fcmj (resistência média esperada) */}
        {pointsFcmj ? (
          <Polyline points={pointsFcmj} stroke="#16a34a" strokeWidth={1} strokeDasharray="2 2" fill="none" />
        ) : null}

        {/* Linha preta: Fcj medido (some inteiramente se não houver nenhum CP com resultado) */}
        {pontosMedidosValidos.length > 0 ? (
          <>
            <Polyline points={pointsFcj} stroke="#000" strokeWidth={1.25} fill="none" />
            {rotulosFcj.map(({ p, index, labelY }) => {
              // Perto da borda direita, o rótulo "XX,XX MPa" à direita do ponto ultrapassaria o
              // SVG e ficaria cortado — nesse caso ele é ancorado à esquerda do ponto.
              const pertoDaBorda = scaleX(p.idade) > width - PADDING.right - 40;
              const labelX = pertoDaBorda ? scaleX(p.idade) - 45 : scaleX(p.idade) - 10;
              return (
                <React.Fragment key={`ponto-${index}-${p.idade}`}>
                  <Circle cx={scaleX(p.idade)} cy={scaleY(p.fcj)} r={2} fill="#000" />
                  <Text
                    x={labelX}
                    y={labelY}
                    style={{ fontSize: LABEL_FONT_SIZE, fontFamily: 'Helvetica-Bold', fill: '#000' }}
                  >
                    {`${formatMpa(p.fcj)} MPa`}
                  </Text>
                </React.Fragment>
              );
            })}
          </>
        ) : null}
      </Svg>

      {/* Legenda */}
      <View style={{ flexDirection: 'row', gap: 16, marginTop: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 10, height: 2, backgroundColor: '#000' }} />
          <Text style={{ fontSize: 6, color: '#444' }}>Fcj (medido)</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 10, height: 2, backgroundColor: '#16a34a' }} />
          <Text style={{ fontSize: 6, color: '#444' }}>Fcmj (resistência média esperada)</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 10, height: 2, backgroundColor: '#dc2626' }} />
          <Text style={{ fontSize: 6, color: '#444' }}>Fcj Esperado</Text>
        </View>
      </View>
    </View>
  );
}

export default CurvaResistenciaChart;
